// src/components1/CategoryDonut.js
import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";

/**
 * Donut for the report tabs.
 *
 * Tapping a slice names it and shows its amount in the middle, in place of the
 * total, rather than in a floating tooltip: on a phone that tooltip lands on top
 * of the total and only ever said "Amount".
 */

// the tapped slice lifts slightly out of the ring
const renderActiveSlice = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        cornerRadius={8}
        fill={fill}
      />
    </g>
  );
};

const CategoryDonut = ({ data = [], total = 0, currency = "$" }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const active = activeIndex != null ? data[activeIndex] : null;
  const multi = data.length > 1;

  const select = (_, index) => setActiveIndex((prev) => (prev === index ? null : index));

  return (
    <div
      className="mb-3"
      style={{
        background: "var(--surface-card)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-card)",
        border: "1px solid var(--border-subtle)",
        textAlign: "center",
        position: "relative",
        padding: "16px 8px 12px 8px",
      }}
    >
      <div style={{ height: 240, position: "relative" }}>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={104}
                cornerRadius={multi ? 8 : 0}
                paddingAngle={multi ? 4 : 0}
                dataKey="value"
                stroke="none"
                activeIndex={activeIndex ?? -1}
                activeShape={renderActiveSlice}
                onClick={select}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
                style={{ cursor: "pointer", outline: "none" }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke="none"
                    style={{ outline: "none" }}
                    opacity={activeIndex == null || activeIndex === index ? 1 : 0.35}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
            No data for this period
          </div>
        )}

        {/* Middle: the tapped category, or the total when nothing is tapped */}
        {data.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              textAlign: "center",
              width: "120px",
            }}
          >
            <div
              style={{
                fontSize: active ? "11px" : "12px",
                fontWeight: 600,
                color: active ? active.color : "var(--text-muted)",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {active ? active.name : "TOTAL"}
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "var(--primary-dark)",
                lineHeight: 1.2,
              }}
            >
              {currency}
              {(active ? active.value : total).toFixed(2)}
            </div>
            {active && (
              <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--text-muted)" }}>
                {active.percent}% of {currency}
                {total.toFixed(2)}
              </div>
            )}
          </div>
        )}
      </div>

      {data.length > 0 && (
        <div style={{ fontSize: "10.5px", color: "var(--text-muted)", marginTop: "2px" }}>
          {active ? "Tap again to see the total" : "Tap a slice for its category total"}
        </div>
      )}
    </div>
  );
};

export default CategoryDonut;
